import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from '@/app/layouts/sidebar/Sidebar';
import { useIsMobile } from '@/shared/lib/use-media-query';
import { useUiStore } from '@/shared/stores/ui.store';

interface AppLayoutProps {
  children: ReactNode;
}

const LAYOUT_EDITOR_PATH = /^\/elabel\/layouts\/[^/]+$/;

export const AppLayout = ({ children }: AppLayoutProps) => {
  const { pathname } = useLocation();
  const isLayoutEditor = LAYOUT_EDITOR_PATH.test(pathname);
  const isMobile = useIsMobile();
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  const wasEditor = useRef(false);
  const savedCollapsed = useRef(false);

  useEffect(() => {
    if (isLayoutEditor === wasEditor.current) {
      return;
    }
    wasEditor.current = isLayoutEditor;
    if (isLayoutEditor) {
      savedCollapsed.current = useUiStore.getState().sidebarCollapsed;
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(savedCollapsed.current);
    }
  }, [isLayoutEditor, setSidebarCollapsed]);

  return (
    <div className="flex max-h-dvh">
      <div
        aria-hidden
        className="fixed inset-x-0 top-0 z-40 h-[env(safe-area-inset-top)] bg-surface"
      />
      {!(isLayoutEditor && isMobile) && <Sidebar />}
      <div className="flex flex-col max-w-full grow items-start">
        <div
          className={`prose max-w-full min-w-0 w-full grow overflow-x-clip overflow-y-auto ${
            isLayoutEditor
              ? 'pt-[env(safe-area-inset-top)]'
              : 'px-4 pb-4 pt-[calc(1rem+env(safe-area-inset-top))]'
          }`}
        >
          <main className="w-full">{children}</main>
        </div>
      </div>
    </div>
  );
};
